import { createAsyncThunk, createSlice, isAnyOf } from '@reduxjs/toolkit'

import { AppRootState, Status } from 'src/store/store'
import axios, { pluckData } from 'src/utilities/http-client'

// ─── Shared types ─────────────────────────────────────────────────────────────

export type AccreditationKey =
  | 'ancc'
  | 'capce'
  | 'virginia'
  | 'ceBrokerFlorida'
  | 'ceBrokerTennessee'
  | 'newJersey'
  | 'newYork'

export type AccreditationHealthStatus =
  | 'missing'
  | 'notPursued'
  | 'noExpirationDate'
  | 'current'
  | 'approaching'
  | 'expired'

export type AccreditationPendingStatus =
  'draft' | 'submitted' | 'approved' | 'rejected'

export type AccreditationHealth = {
  status: AccreditationHealthStatus
  // notPursued cells send these as explicit null rather than omitting the
  // key — widen to accept both since a falsy check treats them the same.
  expirationDate?: string | null
  daysUntilExpiration?: number | null
  pending?: AccreditationPendingStatus | null
}

export type AccreditationTotals = {
  missing: number
  notPursued: number
  noExpirationDate: number
  current: number
  approaching: number
  expired: number
}

export type EventCategory =
  | 'airway|Respiratory|Ventilation'
  | 'cardiovascular'
  | 'trauma'
  | 'medical'
  | 'pediatrics'
  | 'operations'
  | 'critical Care'
  | { other: string }

export let ACCREDITATION_COLUMNS: { key: AccreditationKey; label: string }[] = [
  { key: 'ancc', label: 'ANCC' },
  { key: 'capce', label: 'CAPCE' },
  { key: 'virginia', label: 'Virginia' },
  { key: 'ceBrokerFlorida', label: 'CE Broker FL' },
  { key: 'ceBrokerTennessee', label: 'CE Broker TN' },
  { key: 'newJersey', label: 'New Jersey' },
  { key: 'newYork', label: 'New York' },
]

export type CourseAccreditationHealth = {
  courseID: string
  courseName: string
  courseDisplayName: string
  published: 'published' | 'unpublished'
} & Record<AccreditationKey, AccreditationHealth>

export type EventAccreditationHealth = {
  eventID: string
  eventName: string
  lifecycleState: 'active' | 'preArchive'
  category: EventCategory
} & Record<AccreditationKey, AccreditationHealth>

export type CourseAccreditationDashboard = {
  generatedAt: string
  approachingWindowMonths: number
  courses: CourseAccreditationHealth[]
  totalsByAccreditation: Partial<Record<AccreditationKey, AccreditationTotals>>
  overall: AccreditationTotals
}

export type EventAccreditationDashboard = {
  generatedAt: string
  approachingWindowMonths: number
  events: EventAccreditationHealth[]
  totalsByAccreditation: Partial<Record<AccreditationKey, AccreditationTotals>>
  overall: AccreditationTotals
}

// ─── Row normalization (shared between course and event views) ────────────────

export type AccreditationHealthRow = {
  id: string
  label: string
  secondaryLabel: string
  accreditations: Record<AccreditationKey, AccreditationHealth>
}

let EVENT_CATEGORY_LABELS: Partial<Record<string, string>> = {
  'airway|Respiratory|Ventilation': 'Airway / Respiratory / Ventilation',
  'critical Care': 'Critical Care',
}

export function formatEventCategory(category: EventCategory): string {
  if (typeof category !== 'string') return category.other
  return (
    EVENT_CATEGORY_LABELS[category] ??
    category.charAt(0).toUpperCase() + category.slice(1)
  )
}

export function normalizeCourseRows(
  courses: CourseAccreditationHealth[],
): AccreditationHealthRow[] {
  return courses.map((course) => ({
    id: course.courseID,
    label: course.courseDisplayName || course.courseName,
    secondaryLabel:
      course.published === 'published' ? 'Published' : 'Unpublished',
    accreditations: course,
  }))
}

export function normalizeEventRows(
  events: EventAccreditationHealth[],
): AccreditationHealthRow[] {
  return events.map((event) => ({
    id: event.eventID,
    label: event.eventName,
    secondaryLabel:
      event.lifecycleState === 'preArchive'
        ? 'Pre-archive'
        : formatEventCategory(event.category),
    accreditations: event,
  }))
}

// ─── Thunks ───────────────────────────────────────────────────────────────────

export let fetchCourseAccreditationHealthDashboard = createAsyncThunk(
  'accreditationHealth/fetchCourseAccreditationHealthDashboard',
  () =>
    axios
      .get<CourseAccreditationDashboard>(
        '/api/content-pipeline/accreditation-course/dashboard',
      )
      .then(pluckData),
)

export let fetchEventAccreditationHealthDashboard = createAsyncThunk(
  'accreditationHealth/fetchEventAccreditationHealthDashboard',
  () =>
    axios
      .get<EventAccreditationDashboard>(
        '/api/content-pipeline/accreditation-event/dashboard',
      )
      .then(pluckData),
)

// ─── Not-pursued flag (idempotent toggle) ──────────────────────────────────────

export type AccreditationNotPursuedFlag = {
  id: string
  courseID?: string
  eventID?: string
  slot: AccreditationKey
  actorID: string
  createdTime: string
  updatedTime: string
}

// Refetching the dashboard after these settle is handled by listener
// middleware (see pipeline.middleware.ts) — thunks stay dumb fire-and-forget
// mutations, per this codebase's "don't await/unwrap dispatches" rule.
export let setCourseAccreditationNotPursued = createAsyncThunk(
  'accreditationHealth/setCourseAccreditationNotPursued',
  (args: { courseID: string; slot: AccreditationKey }) =>
    axios
      .post<AccreditationNotPursuedFlag>(
        `/api/content-pipeline/accreditation-course/${args.courseID}/not-pursued/${args.slot}`,
      )
      .then(pluckData),
)

export let clearCourseAccreditationNotPursued = createAsyncThunk(
  'accreditationHealth/clearCourseAccreditationNotPursued',
  (args: { courseID: string; slot: AccreditationKey }) =>
    axios
      .delete(
        `/api/content-pipeline/accreditation-course/${args.courseID}/not-pursued/${args.slot}`,
      )
      .then(() => args),
)

export let setEventAccreditationNotPursued = createAsyncThunk(
  'accreditationHealth/setEventAccreditationNotPursued',
  (args: { eventID: string; slot: AccreditationKey }) =>
    axios
      .post<AccreditationNotPursuedFlag>(
        `/api/content-pipeline/accreditation-event/${args.eventID}/not-pursued/${args.slot}`,
      )
      .then(pluckData),
)

export let clearEventAccreditationNotPursued = createAsyncThunk(
  'accreditationHealth/clearEventAccreditationNotPursued',
  (args: { eventID: string; slot: AccreditationKey }) =>
    axios
      .delete(
        `/api/content-pipeline/accreditation-event/${args.eventID}/not-pursued/${args.slot}`,
      )
      .then(() => args),
)

// Keys not-pursued toggle status by `${courseID|eventID}:${slot}` so a
// component can select "is *this* cell's toggle in flight" without local
// state or awaiting/unwrapping the dispatch itself.
function notPursuedToggleKey(arg: {
  courseID?: string
  eventID?: string
  slot: AccreditationKey
}): string {
  return `${arg.courseID ?? arg.eventID}:${arg.slot}`
}

// ─── State ────────────────────────────────────────────────────────────────────

type AccreditationHealthState = {
  courseDashboard: CourseAccreditationDashboard | null
  courseDashboardStatus: Status
  eventDashboard: EventAccreditationDashboard | null
  eventDashboardStatus: Status
  notPursuedToggleStatus: Record<string, Status>
}

let initialState: AccreditationHealthState = {
  courseDashboard: null,
  courseDashboardStatus: 'uninitialized',
  eventDashboard: null,
  eventDashboardStatus: 'uninitialized',
  notPursuedToggleStatus: {},
}

// ─── Slice ────────────────────────────────────────────────────────────────────

export let accreditationHealthSlice = createSlice({
  name: 'accreditationHealth',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchCourseAccreditationHealthDashboard.pending, (state) => {
        state.courseDashboardStatus = 'pending'
      })
      .addCase(fetchCourseAccreditationHealthDashboard.rejected, (state) => {
        state.courseDashboardStatus = 'rejected'
      })
      .addCase(
        fetchCourseAccreditationHealthDashboard.fulfilled,
        (state, action) => {
          state.courseDashboardStatus = 'fulfilled'
          state.courseDashboard = action.payload
        },
      )
      .addCase(fetchEventAccreditationHealthDashboard.pending, (state) => {
        state.eventDashboardStatus = 'pending'
      })
      .addCase(fetchEventAccreditationHealthDashboard.rejected, (state) => {
        state.eventDashboardStatus = 'rejected'
      })
      .addCase(
        fetchEventAccreditationHealthDashboard.fulfilled,
        (state, action) => {
          state.eventDashboardStatus = 'fulfilled'
          state.eventDashboard = action.payload
        },
      )
      .addMatcher(
        isAnyOf(
          setCourseAccreditationNotPursued.pending,
          clearCourseAccreditationNotPursued.pending,
          setEventAccreditationNotPursued.pending,
          clearEventAccreditationNotPursued.pending,
        ),
        (state, action) => {
          state.notPursuedToggleStatus[notPursuedToggleKey(action.meta.arg)] =
            'pending'
        },
      )
      .addMatcher(
        isAnyOf(
          setCourseAccreditationNotPursued.fulfilled,
          clearCourseAccreditationNotPursued.fulfilled,
          setEventAccreditationNotPursued.fulfilled,
          clearEventAccreditationNotPursued.fulfilled,
        ),
        (state, action) => {
          state.notPursuedToggleStatus[notPursuedToggleKey(action.meta.arg)] =
            'fulfilled'
        },
      )
      .addMatcher(
        isAnyOf(
          setCourseAccreditationNotPursued.rejected,
          clearCourseAccreditationNotPursued.rejected,
          setEventAccreditationNotPursued.rejected,
          clearEventAccreditationNotPursued.rejected,
        ),
        (state, action) => {
          state.notPursuedToggleStatus[notPursuedToggleKey(action.meta.arg)] =
            'rejected'
        },
      )
  },
})

// ─── Selectors ────────────────────────────────────────────────────────────────

export let selectCourseAccreditationHealthDashboard = (state: AppRootState) =>
  state.accreditationHealth.courseDashboard
export let selectCourseAccreditationHealthDashboardStatus = (
  state: AppRootState,
) => state.accreditationHealth.courseDashboardStatus

export let selectEventAccreditationHealthDashboard = (state: AppRootState) =>
  state.accreditationHealth.eventDashboard
export let selectEventAccreditationHealthDashboardStatus = (
  state: AppRootState,
) => state.accreditationHealth.eventDashboardStatus

export let selectAccreditationNotPursuedToggleStatus =
  (id: string, slot: AccreditationKey) => (state: AppRootState) =>
    state.accreditationHealth.notPursuedToggleStatus[
      notPursuedToggleKey(
        // Either key works here — notPursuedToggleKey only cares that one
        // of courseID/eventID is present.
        { courseID: id, slot },
      )
    ]

export default accreditationHealthSlice.reducer
